/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { restartableTask, timeout } from 'ember-concurrency';
import { ref } from 'ember-ref-bucket';
import { getPartialEntityTypesOptions } from 'volta/models/constants/entity-types';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter } from 'volta/utils/filters-utils';
import { strNormalizeAccent } from 'volta/utils/text-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import { SearchEntryResourceName } from '../../models/search-entry';

import type { TError } from 'volta/utils/error-utils';

import type SkuModel from 'volta/models/sku';
import type { IHistoryRecord } from 'volta/routes/base';
import type SearchEntry from '../../models/search-entry';
import type StoreService from 'volta/services/store';
import type AppNavState from 'volta/services/app-nav-state';
import type { IBaseMenuItem } from 'volta/services/app-nav-state';
import type RouterService from '@ember/routing/router-service';
import type { TEntityType } from 'volta/models/types/entity-types';

export const TYPING_DEBOUNCE_MS = 300;

export interface BvAppSearchSignature {
  Args: {
    historyItems?: IHistoryRecord[];
    skuItems?: SkuModel[];
    menuItems?: IBaseMenuItem[];
    onDismiss?: () => void;
  };
}

type TSearchResultKey = 'mostVisited' | 'menu' | 'search';

interface ISearchResults extends Record<TSearchResultKey, unknown[]> {
  mostVisited: IHistoryRecord[];
  menu: (IBaseMenuItem & { id: string })[];
  search: SearchEntry[];
}

const InitialSearchResults: ISearchResults = {
  mostVisited: [],
  menu: [],
  search: []
};

const AllEntityTypes: TEntityType[] = ['skus', 'warehouses', 'suppliers', 'workshops', 'users'];

export default class BvAppSearchComponent extends Component<BvAppSearchSignature> {
  @service appNavState!: AppNavState;
  @service router!: RouterService;
  @service store!: StoreService;

  @tracked results: ISearchResults = InitialSearchResults;
  @tracked pageSearchResults: IBaseMenuItem[] = [];
  @tracked searchEntries: SearchEntry[] = [];
  @tracked selectedEntityTypes: TEntityType[] = AllEntityTypes;
  @tracked selected: number = -1;

  @ref('resultsContainer') resultsContainer: HTMLElement | null = null;

  SearchEntryResourceName = SearchEntryResourceName;
  entityTypeOptions = getPartialEntityTypesOptions(AllEntityTypes);
  allEntityTypes = AllEntityTypes;

  get itemsNodes() {
    return (this.resultsContainer?.querySelectorAll('.search-entry') ?? []) as NodeListOf<
      HTMLButtonElement | HTMLAnchorElement
    >;
  }

  @cached
  get flattenSearchResults() {
    const { mostVisited, menu, search } = this.results;
    return [...mostVisited, ...menu, ...search];
  }

  get selectedItem() {
    return this.flattenSearchResults[this.selected];
  }

  get selectedItemId() {
    return this.selectedItem?.id;
  }

  @action
  handleSearch(query: string) {
    if (query !== this.appNavState.searchText) {
      this.appNavState.searchText = query;
    }
  }

  @action
  handleEntityTypeClick(entityType: TEntityType, isSelected: boolean) {
    this.selectedEntityTypes = isSelected
      ? [...this.selectedEntityTypes, entityType]
      : this.selectedEntityTypes.filter((et) => et !== entityType);
    this.buildResults.perform();
  }

  @action
  handleSelect() {
    const { selected, itemsNodes } = this;
    let index = 0;

    itemsNodes.forEach((_, i) => {
      if (i === selected) {
        index = i;
      }
    });

    const item: HTMLAnchorElement | HTMLButtonElement = itemsNodes[index] as
      | HTMLAnchorElement
      | HTMLButtonElement;

    if (item) {
      item.click();
      this.args.onDismiss?.();
    }
  }

  @action
  handleChangeSelected(direction?: 'up' | 'down') {
    const { selected, itemsNodes } = this;

    let index = 0;
    let newIndex = 0;

    if (direction === 'down') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index === itemsNodes.length - 1 || selected < 0 ? 0 : index + 1;
    } else if (direction === 'up') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index ? index - 1 : itemsNodes.length - 1;
    } else {
      this.selected = 0;
    }

    const newItem: Element = itemsNodes[newIndex] as HTMLAnchorElement | HTMLButtonElement;

    if (newItem && typeof newIndex === 'number') {
      this.selected = newIndex;
      newItem.scrollIntoView({
        behavior: 'smooth',
        block: newIndex ? 'center' : 'end'
      });
    }
  }

  searchTask = restartableTask(async (q: string) => {
    try {
      const query = {
        page: {
          offset: 0,
          limit: 10
        },
        filter: {
          q: eqFilter(q),
          entityType: inFilter(this.selectedEntityTypes)
        }
      };
      await timeout(TYPING_DEBOUNCE_MS);
      this.searchEntries = (
        await this.store.query<SearchEntry>('search-entry', query)
      ).slice() as SearchEntry[];
      return this.searchEntries;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  buildResults = restartableTask(async () => {
    const {
      appNavState: { history, hasSearchText }
    } = this;

    try {
      if (hasSearchText) {
        this.searchMenu();
        await this.searchTask.perform(this.appNavState.searchText as string);
      }

      this.results = {
        mostVisited: hasSearchText ? [] : history.map((h) => ({ ...h, id: h.key })),
        menu: hasSearchText ? this.pageSearchResults.map((h) => ({ ...h, id: h.slug })) : [],
        search: hasSearchText ? this.searchEntries : []
      };
    } catch (error) {
      defaultErrorHandler(error as TError);
    }
  });

  private searchMenu() {
    const pageResults: IBaseMenuItem[] = [];
    const menuLabels = Object.keys(this.appNavState.searchMenuContent);

    menuLabels.forEach((label) => {
      const item = this.appNavState.searchMenuContent[label];
      if (
        item &&
        label.includes(strNormalizeAccent(this.appNavState.searchText?.toLowerCase() ?? ''))
      ) {
        pageResults.push(item);
      }
    });

    this.pageSearchResults = pageResults.length > 10 ? pageResults.slice(-5) : pageResults;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAppSearch: typeof BvAppSearchComponent;
    'bv-app-search': typeof BvAppSearchComponent;
  }
}
